import React, { useState, useEffect } from 'react';

function StorageTest() {
  const [testId] = useState(`test_${Date.now().toString(36)}`);
  const [testValue] = useState(Math.random().toString(36).substring(2, 10));
  
  const [localStorageValue, setLocalStorageValue] = useState(null);
  const [sessionStorageValue, setSessionStorageValue] = useState(null);
  const [urlState, setUrlState] = useState('');
  
  const [localStorageWorks, setLocalStorageWorks] = useState(false);
  const [sessionStorageWorks, setSessionStorageWorks] = useState(false);
  
  // On mount, try to set values in storage
  useEffect(() => {
    try {
      // Write test value to localStorage
      localStorage.setItem(testId, testValue);
      console.log(`Set localStorage[${testId}] = ${testValue}`);
      
      // Write test value to sessionStorage
      sessionStorage.setItem(testId, testValue);
      console.log(`Set sessionStorage[${testId}] = ${testValue}`);
      
      // Read back the values to verify they were set
      setLocalStorageValue(localStorage.getItem(testId));
      setSessionStorageValue(sessionStorage.getItem(testId));
    } catch (error) {
      console.error('Error setting storage values:', error);
    }
  }, [testId, testValue]);
  
  // Check if the values were properly stored
  useEffect(() => {
    if (localStorageValue === testValue) {
      setLocalStorageWorks(true);
    }
    
    if (sessionStorageValue === testValue) {
      setSessionStorageWorks(true);
    }
  }, [localStorageValue, sessionStorageValue, testValue]);
  
  // Helper to clear test values
  const clearStorage = () => {
    try {
      localStorage.removeItem(testId);
      sessionStorage.removeItem(testId);
      setLocalStorageValue(null);
      setSessionStorageValue(null);
      alert('Storage cleared');
    } catch (error) {
      console.error('Error clearing storage:', error);
      alert(`Error clearing storage: ${error.message}`);
    }
  };
  
  // Set a value in both storage types for OAuth testing
  const setupOAuthTest = () => {
    try {
      const stateValue = Math.random().toString(36).substring(2, 10);
      setUrlState(stateValue);
      
      // Set in both storage types
      localStorage.setItem('googleOAuthState', stateValue);
      sessionStorage.setItem('googleOAuthState', stateValue);
      
      // Verify
      const localCheck = localStorage.getItem('googleOAuthState');
      const sessionCheck = sessionStorage.getItem('googleOAuthState');
      
      if (localCheck !== stateValue || sessionCheck !== stateValue) {
        alert(`Warning: Storage values don't match!\nDesired: ${stateValue}\nLocal: ${localCheck}\nSession: ${sessionCheck}`);
      } else {
        alert(`Success! State "${stateValue}" saved to both storage types.\n\nNow use this URL to test OAuth flow:\n\n${window.location.origin}/test/manual-callback?state=${stateValue}`);
      }
    } catch (error) {
      console.error('Error setting OAuth test values:', error);
      alert(`Error setting OAuth test values: ${error.message}`);
    }
  };
  
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Storage Troubleshooting</h1>
      
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-2">Test Info</h2>
        <p className="mb-1"><strong>Test ID:</strong> <code className="bg-gray-100 px-1 py-0.5 rounded">{testId}</code></p>
        <p className="mb-3"><strong>Test Value:</strong> <code className="bg-gray-100 px-1 py-0.5 rounded">{testValue}</code></p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className={`p-3 rounded-md ${localStorageWorks ? 'bg-green-100' : 'bg-red-100'}`}>
            <h3 className="font-medium">localStorage</h3>
            <p className="mb-1">Status: {localStorageWorks ? '✅ Working' : '❌ Not Working'}</p>
            <p className="text-sm"><strong>Stored:</strong> <code>{localStorageValue ?? 'null'}</code></p>
          </div>
          
          <div className={`p-3 rounded-md ${sessionStorageWorks ? 'bg-green-100' : 'bg-red-100'}`}>
            <h3 className="font-medium">sessionStorage</h3>
            <p className="mb-1">Status: {sessionStorageWorks ? '✅ Working' : '❌ Not Working'}</p>
            <p className="text-sm"><strong>Stored:</strong> <code>{sessionStorageValue ?? 'null'}</code></p>
          </div>
        </div>
        
        <button 
          onClick={clearStorage}
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 mr-2"
        >
          Clear Test Storage
        </button>
      </div>
      
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-2">OAuth State Test</h2>
        <p className="mb-4">
          This will set up a manual test of the OAuth state parameter flow.
        </p>
        
        <button 
          onClick={setupOAuthTest}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 mr-2"
        >
          Generate OAuth Test State
        </button>
        
        {urlState && (
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="mb-2"><strong>State Value:</strong> <code className="bg-blue-100 px-1 py-0.5 rounded">{urlState}</code></p>
            <p className="mb-2">A state value has been set in both localStorage and sessionStorage.</p>
            <p className="mb-2">To test the callback, visit this URL:</p>
            <div className="bg-white p-2 rounded border border-blue-200">
              <code className="text-xs break-all">{window.location.origin}/test/manual-callback?state={urlState}</code>
            </div>
          </div>
        )}
      </div>
      
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <h3 className="text-lg font-semibold text-yellow-800">Storage Issues?</h3>
        <ul className="mt-2 list-disc list-inside text-yellow-700">
          <li>Try opening this page in an incognito/private window</li>
          <li>Check if you have browser extensions that might block storage</li>
          <li>Make sure third-party cookies and storage are enabled</li>
          <li>Try a different browser</li>
          <li>Check if your browser is in a special privacy mode</li>
        </ul>
      </div>
    </div>
  );
}

export default StorageTest; 